import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import BackBar from '../components/BackBar';

const API = process.env.REACT_APP_BACKEND_URL;
const getAuthHeader = () => {
  const token = localStorage.getItem('opwmp_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Base floor area per bin (m²) — excludes 20% circulation
// Derived from standard Australian MGB dims + minimal clearance
const BIN_BASE_AREAS = { 120: 0.27, 240: 0.428, 660: 1.155, 1100: 1.71 };
const BIN_SIZES = [120, 240, 660, 1100];
const DEFAULT_FOGO_RATE = 30; // L/unit/week for councils without a published organics rate
const COLLECTIONS_OPTIONS = [
  { value: 0.5, label: 'Fortnightly (0.5)' },
  { value: 1, label: 'Weekly (1)' },
  { value: 2, label: '2× Weekly' },
  { value: 3, label: '3× Weekly' },
];
const COMPACTION_OPTIONS = [
  { value: 1, label: 'None' },
  { value: 2, label: '2:1' },
  { value: 3, label: '3:1' },
];

const DWELLING_TYPES = [
  { key: '1bed', label: '1 Bed / Studio' },
  { key: '2bed', label: '2 Bed' },
  { key: '3bed', label: '3+ Bed' },
  { key: 'individual', label: 'Individual' },
];

const STREAM_LABELS = {
  general: 'General Waste',
  recycling: 'Mixed Recycling',
  fogo: 'FOGO',
  glass: 'Paper & Card',
};

const fmt = (n) => n != null ? n.toLocaleString('en-AU', { maximumFractionDigits: 0 }) : '—';
const fmtArea = (val) => val.toFixed(1);

// Calculate bulky waste area from council-specific rules
const calcBulkyWaste = (rule, totalUnits) => {
  if (!rule || totalUnits <= 0) return 0;
  if (rule.type === 'tiered' && rule.tiers) {
    const tiers = rule.tiers;
    for (let i = 0; i < tiers.length; i++) {
      const t = tiers[i];
      const maxU = t.max_units;
      if (maxU === null || maxU === undefined) {
        // Final open-ended tier with increments
        const base = t.base_area || t.area || 0;
        const prevMax = i > 0 ? (tiers[i - 1].max_units || 0) : 0;
        const extra = t.per_units ? Math.ceil(Math.max(0, totalUnits - prevMax) / t.per_units) * (t.per_area || 0) : 0;
        return base + extra;
      }
      if (totalUnits <= maxU) {
        if (t.per_units && t.base_area !== undefined) {
          const prevMax = i > 0 ? (tiers[i - 1].max_units || 0) : 0;
          return t.base_area + Math.ceil(Math.max(0, totalUnits - prevMax) / t.per_units) * (t.per_area || 0);
        }
        return t.area || 0;
      }
    }
    return 0;
  }
  // Simple or default: base for up to base_units, then increments
  const base = rule.base || 8;
  const baseUnits = rule.base_units || 40;
  const perUnits = rule.per_units || 10;
  const perArea = rule.per_area || 2;
  const max = rule.max || null;
  let area = base;
  if (totalUnits > baseUnits) {
    area += Math.ceil((totalUnits - baseUnits) / perUnits) * perArea;
  }
  if (max && area > max) area = max;
  return area;
};

// Copy helper — writes tab-separated text to clipboard
const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Fallback for older browsers
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
    return true;
  }
};

// ─── WASTE TABLE COMPONENT ───
const WasteTable = ({ title, streams, config, setConfig, generation, showCompaction, showGenOverride, bulkyWasteArea, setBulkyWasteArea, onBulkyManual, bulkyWasteRule }) => {
  const [copiedRow, setCopiedRow] = useState(null);

  const updateStream = (stream, field, value) => {
    setConfig(prev => ({
      ...prev,
      [stream]: { ...prev[stream], [field]: value },
    }));
  };

  const rows = streams.map(stream => {
    const cfg = config[stream];
    if (!cfg) return null;
    const gen = showGenOverride
      ? (cfg.generationOverride !== null && cfg.generationOverride !== undefined && cfg.generationOverride !== ''
        ? parseFloat(cfg.generationOverride)
        : generation[stream] || 0)
      : generation[stream] || 0;
    const { binSize, collections, compaction } = cfg;
    const numBins = gen > 0 ? Math.ceil(gen / (collections * binSize * compaction)) : 0;
    const capacity = Math.round(numBins * binSize * collections * compaction);
    const footprint = numBins * (BIN_BASE_AREAS[binSize] || 0);
    const capacityOk = capacity >= gen;
    const label = STREAM_LABELS[stream] || stream;
    const displayLabel = compaction > 1 ? `${label} (${compaction}:1)` : label;

    return { stream, displayLabel, gen, numBins, capacity, footprint, binSize, collections, compaction, capacityOk, cfg };
  }).filter(Boolean);

  const totalBins = rows.reduce((s, r) => s + r.numBins, 0);
  const totalCapacity = rows.reduce((s, r) => s + r.capacity, 0);
  const totalGen = rows.reduce((s, r) => s + r.gen, 0);
  const totalFootprint = rows.reduce((s, r) => s + r.footprint, 0);
  const totalStorageArea = totalFootprint * 1.2;

  // Copy a single row as tab-separated values (matches Word table column order)
  const copyRow = async (row) => {
    const text = `${row.displayLabel}\t${row.binSize}\t${row.numBins}\t${fmt(row.capacity)}\t${fmt(row.gen)}\t${row.collections}\t${fmtArea(row.footprint)}`;
    await copyToClipboard(text);
    setCopiedRow(row.stream);
    setTimeout(() => setCopiedRow(null), 1500);
  };

  // Copy entire table (all rows + total + storage area + bulky waste)
  const copyTable = async () => {
    const lines = rows.map(r =>
      `${r.displayLabel}\t${r.binSize}\t${r.numBins}\t${fmt(r.capacity)}\t${fmt(r.gen)}\t${r.collections}\t${fmtArea(r.footprint)}`
    );
    lines.push(`Total\t\t${totalBins}\t${fmt(totalCapacity)}\t${fmt(Math.round(totalGen))}\t\t${fmtArea(totalFootprint)} m²`);
    lines.push(`Total Storage Area\t\tPlus 20% Circulation\t\t\t\t${fmtArea(totalStorageArea)} m²`);
    if (bulkyWasteArea !== undefined && bulkyWasteArea > 0) {
      lines.push(`Bulky Waste\t\t\t\t\t\t${bulkyWasteArea} m²`);
    }
    await copyToClipboard(lines.join('\n'));
    setCopiedRow('__table__');
    setTimeout(() => setCopiedRow(null), 1500);
  };

  if (rows.length === 0) return null;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse" data-testid={`table-${title.replace(/\s/g, '-').toLowerCase()}`}>
          <thead>
            <tr className="bg-[#0A2540] text-white">
              <th rowSpan={2} className="px-3 py-2 text-left font-semibold border-r border-white/20 min-w-[200px]">{title}</th>
              <th colSpan={2} className="px-2 py-1.5 text-center font-semibold border-r border-white/20">Bins</th>
              <th rowSpan={2} className="px-3 py-2 text-center font-semibold border-r border-white/20 min-w-[100px]">Weekly<br/>Capacity</th>
              <th rowSpan={2} className="px-3 py-2 text-center font-semibold border-r border-white/20 min-w-[100px]">Weekly<br/>Generation</th>
              <th rowSpan={2} className="px-3 py-2 text-center font-semibold border-r border-white/20 min-w-[110px]">Collections<br/>per Week</th>
              {showCompaction && <th rowSpan={2} className="px-2 py-2 text-center font-semibold border-r border-white/20 min-w-[80px]">Compact.</th>}
              <th rowSpan={2} className="px-3 py-2 text-center font-semibold border-r border-white/20 min-w-[100px]">Total<br/>Footprint m²</th>
              <th rowSpan={2} className="px-1 py-2 text-center w-10">
                <button onClick={copyTable} className="text-white/60 hover:text-white transition-colors" title="Copy entire table" data-testid="copy-table-btn">
                  {copiedRow === '__table__' ? (
                    <svg className="w-4 h-4 mx-auto text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  ) : (
                    <svg className="w-4 h-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                  )}
                </button>
              </th>
            </tr>
            <tr className="bg-[#0f3460] text-white/80 text-xs">
              <th className="px-2 py-1 text-center border-r border-white/20">Size</th>
              <th className="px-2 py-1 text-center border-r border-white/20">No.</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.stream} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`row-${row.stream}`}>
                <td className="px-3 py-2.5 font-medium text-slate-800">{row.displayLabel}</td>
                <td className="px-1 py-1 text-center">
                  <select
                    value={row.binSize}
                    onChange={e => updateStream(row.stream, 'binSize', parseInt(e.target.value))}
                    className="w-[72px] px-1.5 py-1.5 text-sm border border-slate-200 rounded bg-white text-center font-medium tabular-nums focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
                    data-testid={`bin-size-${row.stream}`}
                  >
                    {BIN_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="px-2 py-2 text-center font-bold text-slate-700 tabular-nums">{row.numBins}</td>
                <td className={`px-3 py-2 text-center tabular-nums font-medium ${row.capacityOk ? 'text-slate-700' : 'text-red-600'}`}>{fmt(row.capacity)}</td>
                <td className="px-1 py-1 text-center">
                  {showGenOverride ? (
                    <input
                      type="number"
                      min="0"
                      value={row.cfg.generationOverride !== null && row.cfg.generationOverride !== undefined && row.cfg.generationOverride !== '' ? row.cfg.generationOverride : generation[row.stream] || 0}
                      onChange={e => updateStream(row.stream, 'generationOverride', e.target.value === '' ? null : e.target.value)}
                      className="w-[90px] px-2 py-1.5 text-sm border border-slate-200 rounded text-center tabular-nums focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
                      data-testid={`gen-override-${row.stream}`}
                    />
                  ) : (
                    <span className="text-slate-700 tabular-nums">{fmt(row.gen)}</span>
                  )}
                </td>
                <td className="px-1 py-1 text-center">
                  <select
                    value={row.collections}
                    onChange={e => updateStream(row.stream, 'collections', parseFloat(e.target.value))}
                    className="w-full max-w-[120px] px-1 py-1.5 text-xs border border-slate-200 rounded bg-white text-center focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
                    data-testid={`collections-${row.stream}`}
                  >
                    {COLLECTIONS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </td>
                {showCompaction && (
                  <td className="px-1 py-1 text-center">
                    {row.stream === 'general' ? (
                      <select
                        value={row.compaction}
                        onChange={e => updateStream(row.stream, 'compaction', parseInt(e.target.value))}
                        className="w-[64px] px-1 py-1.5 text-xs border border-slate-200 rounded bg-white text-center focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
                        data-testid="compaction-general"
                      >
                        {COMPACTION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    ) : (
                      <span className="text-slate-400 text-xs">—</span>
                    )}
                  </td>
                )}
                <td className="px-3 py-2 text-center font-bold text-slate-800 tabular-nums">{fmtArea(row.footprint)}</td>
                <td className="px-1 py-1 text-center">
                  <button onClick={() => copyRow(row)} className="text-slate-400 hover:text-blue-600 transition-colors p-1" title={`Copy ${row.displayLabel} row`} data-testid={`copy-row-${row.stream}`}>
                    {copiedRow === row.stream ? (
                      <svg className="w-3.5 h-3.5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                    ) : (
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                    )}
                  </button>
                </td>
              </tr>
            ))}
            {/* Total row */}
            <tr className="bg-slate-100 border-t-2 border-slate-300 font-bold" data-testid="total-row">
              <td className="px-3 py-2 text-slate-700">Total</td>
              <td></td>
              <td className="px-2 py-2 text-center text-slate-800 tabular-nums">{totalBins}</td>
              <td className="px-3 py-2 text-center text-slate-800 tabular-nums">{fmt(totalCapacity)}</td>
              <td className="px-3 py-2 text-center text-slate-800 tabular-nums">{fmt(Math.round(totalGen))}</td>
              <td></td>
              {showCompaction && <td></td>}
              <td className="px-3 py-2 text-center text-slate-800 tabular-nums">{fmtArea(totalFootprint)} m²</td>
              <td className="px-1 py-1 text-center">
                <button onClick={async () => { await copyToClipboard(`Total\t\t${totalBins}\t${fmt(totalCapacity)}\t${fmt(Math.round(totalGen))}\t\t${fmtArea(totalFootprint)} m²`); setCopiedRow('__total__'); setTimeout(() => setCopiedRow(null), 1500); }} className="text-slate-400 hover:text-blue-600 transition-colors p-1" title="Copy Total row" data-testid="copy-row-total">
                  {copiedRow === '__total__' ? (
                    <svg className="w-3.5 h-3.5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  ) : (
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                  )}
                </button>
              </td>
            </tr>
            {/* Total Storage Area row */}
            <tr className="bg-slate-50 font-bold border-t border-slate-200" data-testid="storage-area-row">
              <td className="px-3 py-2.5 text-slate-700">Total Storage Area</td>
              <td colSpan={showCompaction ? 5 : 4} className="px-3 py-2.5 text-center text-slate-500 text-xs italic">Plus 20% circulation room</td>
              <td className="px-3 py-2.5 text-center text-blue-700 font-bold text-base tabular-nums">{fmtArea(totalStorageArea)} m²</td>
              <td className="px-1 py-1 text-center">
                <button onClick={async () => { await copyToClipboard(`Total Storage Area\t\t\tPlus 20% Circulation\t\t\t${fmtArea(totalStorageArea)} m²`); setCopiedRow('__storage__'); setTimeout(() => setCopiedRow(null), 1500); }} className="text-slate-400 hover:text-blue-600 transition-colors p-1" title="Copy Total Storage Area row" data-testid="copy-row-storage">
                  {copiedRow === '__storage__' ? (
                    <svg className="w-3.5 h-3.5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  ) : (
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                  )}
                </button>
              </td>
            </tr>
            {/* Bulky Waste row (only for basement table) */}
            {bulkyWasteArea !== undefined && (
              <tr className="border-t border-slate-200" data-testid="bulky-waste-row">
                <td className="px-3 py-2 text-slate-700 font-medium">Bulky Waste</td>
                <td colSpan={showCompaction ? 5 : 4} className="px-3 py-2 text-[10px] text-slate-400 italic">
                  {bulkyWasteRule ? (
                    <span>
                      {bulkyWasteRule.type === 'default' ? 'Default rule (confirm with council)' : 'Council DCP rule'}
                      {bulkyWasteRule.notes && <span title={bulkyWasteRule.notes}> — {bulkyWasteRule.notes.slice(0, 60)}{bulkyWasteRule.notes.length > 60 ? '...' : ''}</span>}
                    </span>
                  ) : ''}
                </td>
                <td className="px-1 py-1 text-center">
                  <input
                    type="number" min="0" step="1"
                    value={bulkyWasteArea}
                    onChange={e => { setBulkyWasteArea(parseInt(e.target.value) || 0); if (onBulkyManual) onBulkyManual(); }}
                    className="w-[80px] px-2 py-1 text-sm border border-slate-200 rounded text-center font-bold tabular-nums focus:ring-1 focus:ring-blue-400"
                    data-testid="bulky-waste-input"
                  /> m²
                </td>
                <td className="px-1 py-1 text-center">
                  {bulkyWasteArea > 0 && (
                    <button onClick={async () => { await copyToClipboard(`Bulky Waste\t\t\t\t\t\t${bulkyWasteArea} m²`); setCopiedRow('__bulky__'); setTimeout(() => setCopiedRow(null), 1500); }} className="text-slate-400 hover:text-blue-600 transition-colors p-1" title="Copy Bulky Waste row" data-testid="copy-row-bulky">
                      {copiedRow === '__bulky__' ? (
                        <svg className="w-3.5 h-3.5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      ) : (
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                      )}
                    </button>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── MAIN CALCULATOR ───
const AdminResidentialCalculator = () => {
  const navigate = useNavigate();
  const [councils, setCouncils] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCouncil, setSelectedCouncil] = useState(null);
  const [councilRates, setCouncilRates] = useState(null);
  const [projectName, setProjectName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const [unitMix, setUnitMix] = useState({ '1bed': 0, '2bed': 0, '3bed': 0, individual: 0 });
  const [basementConfig, setBasementConfig] = useState({});
  const [chuteEnabled, setChuteEnabled] = useState(false);
  const [chuteConfig, setChuteConfig] = useState({});
  const [includeBinWash, setIncludeBinWash] = useState(false);
  const [fogoEnabled, setFogoEnabled] = useState(false);
  const [bulkyWasteArea, setBulkyWasteArea] = useState(0);
  const [bulkyWasteManual, setBulkyWasteManual] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  // Download PDF
  const downloadPdf = async () => {
    if (!selectedCouncil || !councilRates) return;
    setPdfLoading(true);
    try {
      const payload = {
        council_name: selectedCouncil.council_name,
        unit_mix: unitMix,
        basement: basementConfig,
        chute: chuteEnabled ? Object.fromEntries(
          Object.entries(chuteConfig).filter(([, v]) => v.enabled).map(([k, v]) => [k, { ...v, generation: v.generationOverride || weeklyGeneration[k] || 0 }])
        ) : {},
        include_bin_wash: includeBinWash,
        bulky_waste_area: bulkyWasteArea,
        project_name: projectName,
      };
      const res = await axios.post(`${API}/api/admin/calculator/pdf`, payload, {
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = projectName ? `Waste_Calculator_${projectName.replace(/\s/g, '_')}.pdf` : `Waste_Calculator_${selectedCouncil.council_name.replace(/\s/g, '_')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF generation failed:', err);
    }
    setPdfLoading(false);
  };

  // Fetch council list
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/admin/calculator/councils`, { headers: getAuthHeader() });
        setCouncils(res.data);
      } catch (err) { if (err.response?.status === 403) navigate('/dashboard'); }
      setLoading(false);
    })();
  }, [navigate]);

  // Click outside to close dropdown
  useEffect(() => {
    if (!showDropdown) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showDropdown]);

  // Fetch full rates when council selected
  const fetchCouncilRates = useCallback(async (councilName) => {
    try {
      const res = await axios.get(`${API}/api/admin/calculator-rates/${encodeURIComponent(councilName)}`, { headers: getAuthHeader() });
      setCouncilRates(res.data);
      const rates = res.data.rates || {};
      const freq = res.data.collection_frequency || {};
      const councilHasFogo = res.data.has_fogo || DWELLING_TYPES.some(dt => (rates[dt.key]?.fogo ?? 0) > 0);

      // Determine streams that have council-specific rates
      const streamsWithRates = Object.keys(STREAM_LABELS).filter(s =>
        DWELLING_TYPES.some(dt => (rates[dt.key]?.[s] ?? 0) > 0)
      );

      const bConfig = {};
      const cConfig = {};
      // Initialize all streams that have rates
      streamsWithRates.forEach(s => {
        const defaultCollections = freq[s] ? parseFloat(freq[s]) : (s === 'fogo' ? 1 : s === 'general' ? 1 : 0.5);
        bConfig[s] = { binSize: 660, collections: defaultCollections, compaction: 1 };
        if (s !== 'general') {
          cConfig[s] = { enabled: false, binSize: 240, collections: defaultCollections, compaction: 1, generationOverride: null };
        }
      });

      // Always ensure FOGO has a config entry (even when council doesn't mandate it)
      if (!bConfig.fogo) {
        bConfig.fogo = { binSize: 660, collections: 1, compaction: 1 };
      }
      if (!cConfig.fogo) {
        cConfig.fogo = { enabled: false, binSize: 240, collections: 1, compaction: 1, generationOverride: null };
      }

      setBasementConfig(bConfig);
      setChuteConfig(cConfig);
      setFogoEnabled(councilHasFogo);
    } catch { setCouncilRates(null); }
  }, []);

  const selectCouncil = (council) => {
    setSelectedCouncil(council);
    setSearchQuery(council.council_name);
    setShowDropdown(false);
    setBulkyWasteManual(false);
    fetchCouncilRates(council.council_name);
  };

  const totalUnits = useMemo(() => Object.values(unitMix).reduce((s, v) => s + (parseInt(v) || 0), 0), [unitMix]);

  // Auto-calculate bulky waste when council or units change (unless manually overridden)
  useEffect(() => {
    if (!bulkyWasteManual && councilRates?.bulky_waste && totalUnits > 0) {
      setBulkyWasteArea(calcBulkyWaste(councilRates.bulky_waste, totalUnits));
    }
  }, [councilRates, totalUnits, bulkyWasteManual]);

  const activeStreams = useMemo(() => {
    if (!councilRates?.rates) return [];
    const streams = Object.keys(STREAM_LABELS).filter(s =>
      DWELLING_TYPES.some(dt => (councilRates.rates[dt.key]?.[s] ?? 0) > 0)
    );
    // Always include FOGO when toggled on, even if council has no published rate
    if (fogoEnabled && !streams.includes('fogo')) {
      // Insert after recycling for consistent ordering
      const idx = streams.indexOf('recycling');
      streams.splice(idx >= 0 ? idx + 1 : streams.length, 0, 'fogo');
    }
    // Remove FOGO if toggled off and it was only there from council data
    if (!fogoEnabled && streams.includes('fogo')) {
      return streams.filter(s => s !== 'fogo');
    }
    return streams;
  }, [councilRates, fogoEnabled]);

  // Weekly generation per stream (from unit mix × rates, with FOGO default fallback)
  const weeklyGeneration = useMemo(() => {
    if (!councilRates?.rates) return {};
    const gen = {};
    activeStreams.forEach(stream => {
      let total = 0;
      DWELLING_TYPES.forEach(dt => {
        const councilRate = councilRates.rates[dt.key]?.[stream] || 0;
        // Use default FOGO rate (30L) when council has no published organics rate
        const rate = councilRate > 0 ? councilRate : (stream === 'fogo' ? DEFAULT_FOGO_RATE : 0);
        total += (parseInt(unitMix[dt.key]) || 0) * rate;
      });
      gen[stream] = Math.round(total);
    });
    return gen;
  }, [councilRates, unitMix, activeStreams]);

  // Active chute streams (enabled ones)
  const activeChuteStreams = useMemo(() =>
    Object.keys(chuteConfig).filter(s => chuteConfig[s]?.enabled),
  [chuteConfig]);

  // Basement streams = all active streams
  const basementStreams = useMemo(() =>
    activeStreams.filter(s => basementConfig[s]),
  [activeStreams, basementConfig]);

  // Filtered councils for search
  const filteredCouncils = useMemo(() => {
    if (!searchQuery.trim()) return councils;
    const q = searchQuery.toLowerCase();
    return councils.filter(c => c.council_name.toLowerCase().includes(q) || c.state.toLowerCase().includes(q));
  }, [councils, searchQuery]);

  // Grand totals
  const grandTotals = useMemo(() => {
    let totalArea = 0;
    // Basement
    basementStreams.forEach(stream => {
      const cfg = basementConfig[stream];
      if (!cfg) return;
      const gen = weeklyGeneration[stream] || 0;
      const numBins = gen > 0 ? Math.ceil(gen / (cfg.collections * cfg.binSize * cfg.compaction)) : 0;
      totalArea += numBins * (BIN_BASE_AREAS[cfg.binSize] || 0);
    });
    const basementArea = totalArea * 1.2;
    let chuteArea = 0;
    if (chuteEnabled) {
      let chuteFootprint = 0;
      activeChuteStreams.forEach(stream => {
        const cfg = chuteConfig[stream];
        if (!cfg) return;
        const gen = cfg.generationOverride !== null && cfg.generationOverride !== undefined && cfg.generationOverride !== ''
          ? parseFloat(cfg.generationOverride)
          : weeklyGeneration[stream] || 0;
        const numBins = gen > 0 ? Math.ceil(gen / (cfg.collections * cfg.binSize * cfg.compaction)) : 0;
        chuteFootprint += numBins * (BIN_BASE_AREAS[cfg.binSize] || 0);
      });
      chuteArea = chuteFootprint * 1.2;
    }
    const binWash = includeBinWash ? 3.0 : 0;
    const bulky = bulkyWasteArea || 0;
    return { basementArea: Math.round(basementArea * 10) / 10, chuteArea: Math.round(chuteArea * 10) / 10, binWash, bulky, total: Math.round((basementArea + chuteArea + binWash + bulky) * 10) / 10 };
  }, [basementStreams, basementConfig, weeklyGeneration, chuteEnabled, activeChuteStreams, chuteConfig, includeBinWash, bulkyWasteArea]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" /></div>;

  const hasResults = totalUnits > 0 && activeStreams.length > 0 && basementStreams.length > 0;

  return (
    <div className="min-h-screen bg-[#F8FAFC]" style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }} data-testid="residential-calculator-page">
      <link href="https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
      <BackBar to="/admin" label="Admin" />

      <header className="bg-white border-b border-slate-200/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-[#0A2540]" data-testid="calculator-title">Residential Waste Calculator</h1>
            <p className="text-sm text-slate-500 mt-0.5">Calculate bin requirements and waste room floor area</p>
          </div>
          {hasResults && (
            <button
              onClick={downloadPdf}
              disabled={pdfLoading}
              className="flex items-center gap-2 px-4 py-2.5 bg-[#0A2540] text-white text-sm font-medium rounded-lg hover:bg-[#0f3460] disabled:opacity-50 transition-colors"
              data-testid="download-pdf-btn"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
              {pdfLoading ? 'Generating...' : 'PDF'}
            </button>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-5">
        {/* ─── COUNCIL & PROJECT ─── */}
        <section className="bg-white rounded-xl border border-slate-100 shadow-sm p-5" data-testid="step-council">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative md:col-span-2" ref={dropdownRef}>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Council (LGA)</label>
              <input
                type="text" value={searchQuery}
                onChange={e => { setSearchQuery(e.target.value); setShowDropdown(true); if (!e.target.value) { setSelectedCouncil(null); setCouncilRates(null); } }}
                onFocus={() => setShowDropdown(true)}
                placeholder="Search councils..."
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                data-testid="council-search-input"
                autoComplete="off"
              />
              {showDropdown && filteredCouncils.length > 0 && (
                <div className="absolute z-30 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-xl max-h-60 overflow-y-auto" data-testid="council-dropdown">
                  {filteredCouncils.map(c => (
                    <button key={c.council_name} onClick={() => selectCouncil(c)}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 flex items-center justify-between ${selectedCouncil?.council_name === c.council_name ? 'bg-blue-50 text-blue-700' : 'text-slate-700'}`}
                      data-testid={`council-option-${c.council_name.replace(/\s/g, '-')}`}
                    >
                      <span>{c.council_name}</span>
                      <span className="flex items-center gap-1.5">
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${c.state === 'NSW' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}`}>{c.state}</span>
                        {c.verified && <span className="text-green-500 text-xs font-bold">✓</span>}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Project Name</label>
              <input type="text" value={projectName} onChange={e => setProjectName(e.target.value)}
                placeholder="e.g. 42 Smith Street"
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                data-testid="project-name-input" />
            </div>
          </div>
          {selectedCouncil && councilRates && (
            <div className="flex items-center gap-3 text-xs text-slate-500 mt-3 pt-3 border-t border-slate-100">
              <span className={`font-bold px-1.5 py-0.5 rounded ${selectedCouncil.state === 'NSW' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}`}>{selectedCouncil.state}</span>
              {activeStreams.map(s => <span key={s} className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-medium">{STREAM_LABELS[s]}{s === 'fogo' && !councilRates.has_fogo && ' (default)'}</span>)}
              {selectedCouncil.verified ? <span className="text-green-600 font-medium">Verified</span> : <span className="text-amber-600 font-medium">Unverified</span>}
            </div>
          )}
        </section>

        {/* ─── UNIT MIX ─── */}
        {selectedCouncil && councilRates && (
          <section className="bg-white rounded-xl border border-slate-100 shadow-sm p-5" data-testid="step-unit-mix">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-bold text-[#0A2540]">Unit Mix</h2>
              <span className="text-xs text-slate-500">Total: <span className="font-bold text-[#0A2540]">{totalUnits} units</span></span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {DWELLING_TYPES.map(dt => (
                <div key={dt.key}>
                  <label className="block text-xs text-slate-600 mb-1">{dt.label}</label>
                  <input type="number" min="0" value={unitMix[dt.key]}
                    onChange={e => setUnitMix(prev => ({ ...prev, [dt.key]: e.target.value === '' ? 0 : parseInt(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg text-center font-medium tabular-nums focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                    data-testid={`unit-mix-${dt.key}`} />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ─── FOGO TOGGLE ─── */}
        {selectedCouncil && councilRates && totalUnits > 0 && (
          <div className="flex items-center gap-4 px-1" data-testid="fogo-toggle-section">
            <label className="flex items-center gap-2.5 cursor-pointer group" data-testid="fogo-toggle">
              <div className="relative">
                <input type="checkbox" checked={fogoEnabled} onChange={e => setFogoEnabled(e.target.checked)} className="sr-only peer" />
                <div className="w-9 h-5 bg-slate-200 rounded-full peer-checked:bg-green-600 transition-colors" />
                <div className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm peer-checked:translate-x-4 transition-transform" />
              </div>
              <span className="text-sm font-semibold text-[#0A2540] group-hover:text-green-700 transition-colors">FOGO / Organics</span>
            </label>
            {fogoEnabled && !councilRates.has_fogo && (
              <span className="text-[11px] text-amber-600 bg-amber-50 px-2 py-0.5 rounded font-medium">
                Using default rate: {DEFAULT_FOGO_RATE}L / unit / week (no published council rate)
              </span>
            )}
            {fogoEnabled && councilRates.has_fogo && (
              <span className="text-[11px] text-green-600 bg-green-50 px-2 py-0.5 rounded font-medium">
                Council-specific FOGO rates
              </span>
            )}
            {!fogoEnabled && (
              <span className="text-[11px] text-slate-400">Toggle on to include organics stream</span>
            )}
          </div>
        )}

        {/* ─── BASEMENT / WASTE STORAGE ROOM TABLE ─── */}
        {hasResults && (
          <WasteTable
            title="Residential Waste Storage Room"
            streams={basementStreams}
            config={basementConfig}
            setConfig={setBasementConfig}
            generation={weeklyGeneration}
            showCompaction={basementStreams.includes('general')}
            showGenOverride={false}
            bulkyWasteArea={bulkyWasteArea}
            setBulkyWasteArea={setBulkyWasteArea}
            onBulkyManual={() => setBulkyWasteManual(true)}
            bulkyWasteRule={councilRates?.bulky_waste}
          />
        )}

        {/* ─── CHUTE ROOM TOGGLE & TABLE ─── */}
        {hasResults && (
          <section data-testid="chute-section">
            <div className="flex items-center gap-4 mb-3">
              <label className="flex items-center gap-2.5 cursor-pointer group" data-testid="chute-toggle">
                <div className="relative">
                  <input type="checkbox" checked={chuteEnabled} onChange={e => setChuteEnabled(e.target.checked)} className="sr-only peer" />
                  <div className="w-9 h-5 bg-slate-200 rounded-full peer-checked:bg-blue-600 transition-colors" />
                  <div className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm peer-checked:translate-x-4 transition-transform" />
                </div>
                <span className="text-sm font-semibold text-[#0A2540] group-hover:text-blue-700 transition-colors">Residential Floor — Chute Rooms</span>
              </label>
            </div>

            {chuteEnabled && (
              <>
                {/* Stream toggles for chute room */}
                <div className="flex flex-wrap gap-3 mb-3 pl-1">
                  {Object.keys(chuteConfig).map(stream => (
                    <label key={stream} className="flex items-center gap-1.5 text-xs cursor-pointer">
                      <input type="checkbox" checked={chuteConfig[stream]?.enabled || false}
                        onChange={e => setChuteConfig(prev => ({ ...prev, [stream]: { ...prev[stream], enabled: e.target.checked } }))}
                        className="w-3.5 h-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        data-testid={`chute-enable-${stream}`} />
                      <span className="text-slate-700 font-medium">{STREAM_LABELS[stream]}</span>
                    </label>
                  ))}
                  <span className="text-[10px] text-slate-400 self-center ml-2">Generation values are editable — adjust for chute room volumes</span>
                </div>

                {activeChuteStreams.length > 0 && (
                  <WasteTable
                    title="Residential Floor — Chute Rooms"
                    streams={activeChuteStreams}
                    config={chuteConfig}
                    setConfig={setChuteConfig}
                    generation={weeklyGeneration}
                    showCompaction={false}
                    showGenOverride={true}
                  />
                )}
              </>
            )}
          </section>
        )}

        {/* ─── BIN WASH & GRAND SUMMARY ─── */}
        {hasResults && (
          <section className="bg-[#0A2540] rounded-xl p-5 text-white" data-testid="grand-summary">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-bold">Summary{projectName ? ` — ${projectName}` : ''}</h2>
              <label className="flex items-center gap-2 cursor-pointer text-xs" data-testid="bin-wash-toggle">
                <div className="relative">
                  <input type="checkbox" checked={includeBinWash} onChange={e => setIncludeBinWash(e.target.checked)} className="sr-only peer" />
                  <div className="w-8 h-4 bg-white/20 rounded-full peer-checked:bg-blue-500 transition-colors" />
                  <div className="absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full shadow-sm peer-checked:translate-x-4 transition-transform" />
                </div>
                <span className="text-white/70">Bin Wash Bay (3m²)</span>
              </label>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold tabular-nums">{grandTotals.basementArea} m²</div>
                <div className="text-[11px] text-slate-300 mt-0.5">Storage Room (incl. 20%)</div>
              </div>
              {chuteEnabled && grandTotals.chuteArea > 0 && (
                <div>
                  <div className="text-2xl font-bold tabular-nums">{grandTotals.chuteArea} m²</div>
                  <div className="text-[11px] text-slate-300 mt-0.5">Chute Rooms (incl. 20%)</div>
                </div>
              )}
              {includeBinWash && (
                <div>
                  <div className="text-2xl font-bold tabular-nums">3.0 m²</div>
                  <div className="text-[11px] text-slate-300 mt-0.5">Bin Wash Bay</div>
                </div>
              )}
              {grandTotals.bulky > 0 && (
                <div>
                  <div className="text-2xl font-bold tabular-nums">{grandTotals.bulky} m²</div>
                  <div className="text-[11px] text-slate-300 mt-0.5">Bulky Waste</div>
                </div>
              )}
              <div>
                <div className="text-2xl font-bold text-blue-300 tabular-nums">{grandTotals.total} m²</div>
                <div className="text-[11px] text-slate-300 mt-0.5">Total Required Area</div>
              </div>
            </div>
            <div className="mt-4 pt-3 border-t border-white/10 text-[10px] text-white/40">
              Floor area per bin (base): {BIN_SIZES.map(s => `${s}L = ${BIN_BASE_AREAS[s]}m²`).join(' · ')} · 20% circulation applied to totals
            </div>
          </section>
        )}

        {/* Empty state */}
        {!selectedCouncil && (
          <div className="text-center py-16 text-slate-400">
            <svg className="w-12 h-12 mx-auto mb-3 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            <p className="text-sm">Select a council above to start calculating</p>
            <p className="text-xs text-slate-300 mt-1">{councils.length} councils with rate data</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminResidentialCalculator;
